import { AsyncWorker } from 'typescript-fsa-redux-thunk';
import { WithStatus } from '../../../models';
import { PostEventBatchRequest } from '../../../models/requests';
import { withStatus } from '../../../utils';
import { RootState } from '../../models';
import { isIOS } from 'react-device-detect';

export const postEventsWorker =
	(apiRoot: string): AsyncWorker<PostEventBatchRequest, WithStatus<{}>, RootState> =>
	async ({ async, events }, dispatch, getState) => {
		const data = getState().data;

		if (!data.isLoaded) {
			throw new Error('Load user data before trying to send events!');
		}

		const url = `${apiRoot}/events/${data.sessionId}`;
		const body = JSON.stringify({ async, events });

		if (async) {
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body,
			});

			if (!response.ok) {
				throw new Error('Failed to send events!');
			}

			const result = withStatus({
				ok: response.ok,
				status: response.status,
			})({});

			return result;
		} else {
			console.log('Sending events synchronously...'); //remove

			// 1) Try sendBeacon when not on iOS
			if (!isIOS && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
				try {
					const blob = new Blob([body], { type: 'application/json' });
					const ok = navigator.sendBeacon(url, blob);
					if (ok) return withStatus({ ok: true, status: 204 })({});
				} catch (error) {
					console.error(`Error sending beacon with events: ${error}`);
				}
			}

			// 2) Try fetch with keepalive (works better on iOS during dismissal)
			try {
				if (typeof fetch === 'function') {
					await fetch(url, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body,
						keepalive: true,
						mode: 'cors',
						credentials: 'omit',
					});
					// We can't reliably read the response during dismissal; assume best-effort OK
					return withStatus({ ok: true, status: 204 })({});
				}
			} catch (error) {
				// swallow; fall through to sync XHR
				console.error(`Error fetching events synchronously: ${error}`);
			}

			// 3) Fallback to synchronous XHR
			const xhr = new XMLHttpRequest();
			xhr.open('POST', url, false);
			xhr.setRequestHeader('Content-Type', 'application/json');

			const responseHandler = () => {
				return withStatus({
					ok: xhr.status >= 200 && xhr.status <= 299,
					status: xhr.status,
				})({});
			};

			const request = new Promise<any>(function (resolve, reject) {
				xhr.onload = resolve;
				xhr.onerror = reject;
			}).then(responseHandler, responseHandler);

			try {
				xhr.send(body);
			} catch (error) {
				console.error(`Error sending events synchronously: ${error}`);
			}
			return request;
		}
	};
